/* eslint-disable max-classes-per-file, lines-between-class-members, no-param-reassign */
import axios from 'axios';
import { getCookie } from 'app/libs/cookie';
import { ACCESS_TOKEN_KEY } from 'app/libs/auth';
import { clearAuthInfo } from 'app/hook/login/useLogout';
import { goHome } from 'app/libs/location';
import { uaParser as ua } from '@encarpkg/shared';
import { isBrowser } from 'app/libs/environment';

export const API_AUTH_URL = process.env.REACT_APP_API_AUTH;
export const API_URL = process.env.REACT_APP_API_URL;
export const API_LOAN_URL = process.env.REACT_APP_API_LOAN;
export const API_MEYLE_URL = process.env.REACT_APP_API_MEYLE;
export const API_NAS_URL = process.env.REACT_APP_API_NAS;

export const DEPLOY_ENV = process.env.REACT_APP_DEPLOY_ENV;
export const MOBILE_TOKEN = process.env.REACT_APP_TOKEN_MOBILE;

class HttpClient {
	instance = null;
	baseURL = null;
	unAuthorized = null;

	static getApiUrl(stageURL) {
		let url = API_URL;
		if (stageURL && HttpClient.isStageEnv()) url = stageURL;
		return url;
	}

	static authentication() {
		const authToken = getCookie('X-Auth-Token-Encar');

		return axios.get(`${API_AUTH_URL}/site`, {
			headers: {
				'Content-Type': 'application/json charset=UTF-8',
				'X-Auth-Token': authToken,
			},
		});
	}

	static isStageEnv() {
		return !['dev', 'staging', 'prod'].includes(DEPLOY_ENV);
	}

	static createBaseParams(config) {
		const isGetFetchByIE = isBrowser && ua().isIE && config?.method === 'get';

		return {
			...config.params,
			...(isGetFetchByIE && { timestampForIECash: Date.now() }),
		};
	}

	constructor() {
		this.unAuthorized = false;
		this.init(API_URL);
	}

	init(baseURL = '') {
		this.baseURL = baseURL;
		this.instance = axios.create({
			baseURL,
		});
		if (isBrowser) {
			this.#captureStackTrace();
		}
		this.instance.interceptors.request.use(
			async (config) => {
				const params = HttpClient.createBaseParams(config);

				if (config.useAuth) {
					try {
						const { data } = await HttpClient.authentication();
						return {
							headers: { Authorization: `Bearer ${data.encarApiStaticKey}` },
							params,
							...config,
						};
					} catch (error) {
						const { status } = error.response;

						if (status === 400 && !this.unAuthorized) {
							this.unAuthorized = true;
							alert('유효하지 않은 세션입니다.');
							clearAuthInfo();

							if (process.env.REACT_APP_DEPLOY_ENV !== 'local') goHome();
						}

						return Promise.reject(error);
					}
				} else {
					return {
						...config,
						params,
					};
				}
			},
			(error) => Promise.reject(error),
		);
	}

	setBaseURL(stageURL) {
		let url = this.baseURL;

		if (stageURL && HttpClient.isStageEnv()) url = stageURL;

		this.instance.defaults.baseURL = url;

		return this;
	}

	withToken() {
		this.instance.interceptors.request.use((config) => ({
			...config,
			headers: { Authorization: `Bearer ${getCookie(ACCESS_TOKEN_KEY)}` },
		}));

		return this;
	}

	#wrapRequest(method) {
		const original = this.instance[method];
		return (...args) => {
			let capturedStackTrace = new Error().stack ?? '';
			return original.call(this.instance, ...args).catch((error) => {
				if (error.stack) {
					// error.stack과 하나의 stack trace로 합치기위해 의미없는 스택라인 제거
					capturedStackTrace = capturedStackTrace.split('\n').slice(2).join('\n');
					error.stack = `${error.stack}\n${capturedStackTrace}`;
				}
				throw error;
			});
		};
	}

	#captureStackTrace() {
		Object.assign(this.instance, {
			option: this.#wrapRequest('options'),
			head: this.#wrapRequest('head'),
			get: this.#wrapRequest('get'),
			patch: this.#wrapRequest('patch'),
			post: this.#wrapRequest('post'),
			put: this.#wrapRequest('put'),
			delete: this.#wrapRequest('delete'),
		});
	}

	create() {
		return this.instance;
	}
}

export class HttpAuthClient extends HttpClient {
	constructor() {
		super();
		super.init(API_AUTH_URL);
	}
}

export class HttpLoanClient extends HttpClient {
	constructor() {
		super();
		super.init(API_LOAN_URL);
	}
}

export class HttpNasClient extends HttpClient {
	constructor() {
		super();
		super.init(API_NAS_URL);
	}
}

export class HttpMeyleClient extends HttpClient {
	constructor() {
		super();
		super.init(API_MEYLE_URL);
	}
}

export class HttpExternalClient extends HttpClient {
	constructor() {
		super();
		super.init();
	}
}

export default HttpClient;
